import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom"; // Import Link
import { useAuth } from "../useAuth"; // Adjust path to your useAuth hook
import { useFeedbackForm } from "../components/useFeedbackForm";
import RatingInput from "../components/RatingInput";
import Alert from "../components/Alert";
import FeedbackCard from "../components/FeedbackCard";

const FeedbackPage = () => {
  const { t } = useTranslation("FeedbackPage");
  const { currentUser } = useAuth(); // Get current user status
  const {
    feedbackText,
    setFeedbackText,
    rating,
    setRating,
    isLoading,
    error,
    success,
    submitFeedback,
  } = useFeedbackForm();
  const location = useLocation();

  const handleSubmit = (event) => {
    event.preventDefault();
    submitFeedback();
  };

  return (
    <FeedbackCard>
      {/* Logo and Title */}
      <div className="flex justify-center mb-8">
        <img
          src="http://cdn.mcauto-images-production.sendgrid.net/ec76b30417c12816/e1fcff87-e336-4192-8f15-47554bd2f801/500x500.png"
          alt={t("logo_alt", "ProRecruit Logo")} // Added translation key
          className="h-16 w-auto"
        />
      </div>
      <h2 className="text-center text-3xl font-extrabold text-slate-900 font-bold tracking-tight mb-2">
        {t("feedback_title")}
      </h2>
      <p className="text-center text-sm text-slate-600 leading-relaxed mb-8">
        {t("feedback_description")}
      </p>

      {/* --- Feedback Form --- */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Rating Input */}
        <div className="flex flex-col items-center">
          <label
            id="rating-label"
            className="block text-sm font-medium text-slate-600 leading-relaxed mb-2"
          >
            {" "}
            {/* Added id */}
            {t("rating_label")}
          </label>
          <RatingInput
            value={rating}
            onChange={setRating}
            aria-labelledby="rating-label"
          />
        </div>

        {/* Feedback Text Area */}
        <div>
          <label
            htmlFor="feedback-text"
            className="block text-sm font-medium text-slate-600 leading-relaxed"
          >
            {t("feedback_text_label")}
          </label>
          <div className="mt-1">
            <textarea
              id="feedback-text"
              name="feedback-text"
              rows={4}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder={t("feedback_text_placeholder")}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              required
              aria-required="true"
            />
          </div>
        </div>

        {/* Display Success/Error Messages */}
        <Alert type="error" message={error} className="mt-4" />
        <Alert type="success" message={success} className="mt-4" />

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className={`
              inline-flex justify-center py-2 px-4 border border-transparent shadow-sm
              text-sm font-medium rounded-md text-white
              bg-indigo-600 hover:bg-indigo-700
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {isLoading ? t("submitting") : t("feedback_submit_button")}
          </button>
        </div>
      </form>

      {/* Support and Additional Info */}
      <div className="mt-8 border-t border-gray-200 pt-8 text-center">
        <h3 className="text-lg font-medium text-slate-900 font-bold tracking-tight">
          {t("feedback_support_title")}
        </h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          {t("feedback_support_description")}
          <a
            href="https://app.prorecruit.tech/support"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            {" "}
            {t("feedback_support_link")}{" "}
          </a>
          {t("or")}
          <a
            href="mailto:support@prorecruit.tech"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            {" "}
            {t("feedback_support_email")}{" "}
          </a>
          .
        </p>

        {/* Conditional Dashboard/Sign In Link */}
        <div className="mt-6 text-sm">
          {" "}
          {/* Added margin-top */}
          {currentUser ? (
            <Link
              to="/dashboard"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {t("go_to_dashboard", "Go to your Dashboard")}
            </Link>
          ) : (
            <Link
              to="/signin"
              state={{ from: location }}
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {t("sign_in_link", "Sign in to your account")}
            </Link>
          )}
        </div>

        {/* Privacy Notice */}
        <p className="mt-4 text-xs text-slate-500">
          {" "}
          {/* Adjusted margin-top */}
          {t("feedback_privacy_notice")}
          <a
            href="/privacy"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            {" "}
            {t("feedback_privacy_link")}{" "}
          </a>
          .
        </p>
      </div>
    </FeedbackCard>
  );
};

export default FeedbackPage;
